import React from 'react';
import { t, Trans } from '@lingui/macro';
import { List, Paragraph } from '@mos-cat/typography';
import './MainSteps.pcss';
import Link from '../../shared/Link/Link';
import { i18n } from '../../../helpers/i18n';
import StepContent from './StepContent';
import Step from '@mos-front-components/public/es/components/Steps/private/Step/Step';
import Steps from '@mos-front-components/public/es/components/Steps';

const mainStepsContents = [
  {
    title: <Trans>Подготовьте данные в Личном кабинете</Trans>,
    content: (
      <Paragraph className="MainSteps__paragraph">
        <Trans>
          Заполните в{' '}
          <Link href={i18n._(t`https://my.mos.ru/my/`)} target="_blank">
            Личном кабинете
          </Link>{' '}
          сведения о себе и своих детях. Это необязательно, но облегчит
          заполнение заявления.
        </Trans>
      </Paragraph>
    ),
  },
  {
    title:
      'Подайте заявление о присвоении (продлении) статуса многодетной семьи',
    content: [
      <Paragraph className="MainSteps__paragraph">
        Подайте заявление в <Link to="/new/">электронном виде</Link>.
      </Paragraph>,
      <Paragraph>
        <Trans id="mainPage/howItWorks/applyForCertificate/content/paragraph/2">
          Для получения льготного статуса в семье должно быть не менее трех
          детей. Все несовершеннолетние дети должны быть зарегистрированы по
          месту жительства в Москве.
        </Trans>
      </Paragraph>,
      <Paragraph className="text-strong">
        Новые правила с 1 января 2025 года
      </Paragraph>,
      <Paragraph>
        Согласно Указу Президента Российской Федерации от 23.01.2024 № 63 «О
        мерах социальной поддержки многодетных семей» многодетным семьям
        присваивается льготный статус и предоставляются меры социальной
        поддержки, когда в семье воспитываются трое и более несовершеннолетних
        детей либо студентов, обучающихся по очной форме обучения до достижения
        ими возраста 23 лет.
      </Paragraph>,
      <Paragraph>
        С 1 января 2025 года в столице действует единый порядок, установленный в
        Российской Федерации, согласно которому многодетными являются семьи,
        воспитывающие троих и более несовершеннолетних детей. Это позволяет
        получать льготы до достижения старшим ребенком совершеннолетия или до 23
        лет, если он продолжает обучение на очной форме, включая учебу в вузе.
        Новые правила распространяются на семьи, в которых впервые рождается
        третий ребенок.
      </Paragraph>,
      <Paragraph>
        Для семей, ранее признанных многодетными, прежние льготы сохраняются до
        достижения младшим ребенком 16 лет (до 18 лет, если учится в школе или
        колледже), а если в такой семье появляется еще один ребенок, срок
        предоставления льгот продлевается по младшему новорожденному ребенку.
      </Paragraph>,
    ],
    listTitle: (
      <Trans id="mainPage/howItWorks/applyForCertificate/listTitle">
        Необходимые документы:
      </Trans>
    ),
    list: [
      <Trans id="mainPage/howItWorks/applyForCertificate/list/item/1">
        документ, удостоверяющий личность заявителя
      </Trans>,
      <Trans id="mainPage/howItWorks/applyForCertificate/list/item/2">
        документ, удостоверяющий личность супруга, и свидетельство о заключении
        брака, если заявитель состоит в браке
      </Trans>,
      <Trans id="mainPage/howItWorks/applyForCertificate/list/item/3">
        документ, удостоверяющий личность второго родителя, если проживает
        совместно с заявителем
      </Trans>,
      <Trans id="mainPage/howItWorks/applyForCertificate/list/item/4">
        свидетельство о рождении для каждого из детей
      </Trans>,
      <Trans id="mainPage/howItWorks/applyForCertificate/list/item/5">
        документ, удостоверяющий личность ребёнка, для граждан РФ старше 14 лет
        или иностранных граждан и лиц без гражданства (независимо от возраста)
      </Trans>,
      <Trans id="mainPage/howItWorks/applyForCertificate/list/item/6">
        свидетельство о перемене ФИО для всех, у кого изменялись личные данные
      </Trans>,
      <Trans id="mainPage/howItWorks/applyForCertificate/list/item/7">
        свидетельство об установлении отцовства (если в отношении ребенка
        установлено отцовство)
      </Trans>,
      <Trans id="mainPage/howItWorks/applyForCertificate/list/item/8">
        свидетельство о смерти или судебное решение о признании родителя умершим
        для детей, чьи родители умерли
      </Trans>,
      <Trans id="mainPage/howItWorks/applyForCertificate/list/item/9">
        свидетельство о расторжении брака, если брак со вторым родителем
        расторгнут
      </Trans>,
    ],
    contentAfter: [
      <Paragraph key={'after'}>
        <Trans id="mainPage/howItWorks/applyForCertificate/contentAfterList">
          Указанная в заявлении информация будет проверена путем
          межведомственного взаимодействия.
        </Trans>
      </Paragraph>,
    ],
  },
  {
    title:
      'В случае необходимости принесите документы в центр госуслуг "Мои документы"',
    content: (
      <Paragraph className="MainSteps__paragraph">
        Если информация в заявлении не будет подтверждена, то в{' '}
        <Link href={i18n._(t`https://my.mos.ru/my/`)} target="_blank">
          Личном кабинете{' '}
        </Link>
        вы получите уведомление о необходимости предоставить оригиналы
        документов. Донести необходимые документы можно в любой{' '}
        <Link href={i18n._(t`https://www.mos.ru/pgu/ru/md/`)} target="_blank">
          Центр предоставления государственных услуг города Москвы «Мои
          документы»{' '}
        </Link>
        в течение 10 рабочих дней со дня направления уведомления.
      </Paragraph>
    ),
  },
  {
    title: 'Получите уведомление о присвоении (продлении) льготного статуса',
    content: [
      <Paragraph className="MainSteps__paragraph" key={0}>
        В случае положительного решения уведомление о присвоении (продлении)
        льготного статуса в отношении каждого члена семьи поступит заявителю в{' '}
        <Link href={i18n._(t`https://my.mos.ru/my/`)} target="_blank">
          Личный кабинет
        </Link>
        .
      </Paragraph>,
      <Paragraph className="MainSteps__paragraph" key={1}>
        После присвоения льготного статуса, при желании, вы можете оформить
        удостоверение многодетной семьи города Москвы единого образца на
        бумажном носителе в любом{' '}
        <Link
          href={i18n._(
            t`https://www.mos.ru/services/centry-gosudarstvennyh-uslug/`
          )}
          target="_blank"
        >
          Центре предоставления государственных услуг города Москвы «Мои
          документы»
        </Link>
        .
      </Paragraph>,
      <Paragraph key={2}>
        У членов каждой многодетной семьи в «личном кабинете» на mos.ru
        формируется цифровое удостоверение, которое содержит QR-код для
        подтверждения льготного статуса.
      </Paragraph>,
      <Paragraph key={3}>
        Цифровое удостоверение имеет такую же юридическую силу, что и
        удостоверение на бумажном носителе и может использоваться членами
        многодетной семьи для подтверждения их льготного статуса при получении
        скидок, а также в учреждениях культуры и иных организациях бытового и
        социального обслуживания города Москвы.
      </Paragraph>,
      <Paragraph key={4}>
        На основании переданной информации многодетные семьи города Москвы
        «личном кабинете» на Едином портале государственных услуг формируется
        электронное удостоверение, которое может использоваться, например, для
        посещения федеральных учреждений культуры или учреждений культуры иных
        субъектов РФ.
      </Paragraph>,
    ],
  },
];

function MainSteps(): JSX.Element {
  const mainPageStepsContent = mainStepsContents.map(
    ({ title, ...restProps }, index) => {
      const isStepCompleted = false;
      const key = `${index}_${title}`;
      return (
        <Step
          className="MainSteps__Step"
          key={key}
          completed={isStepCompleted}
          title={<h3 className="MainSteps__StepTitle">{title}</h3>}
        >
          <StepContent {...restProps} />
        </Step>
      );
    }
  );

  return (
    <div className="MainSteps">
      <div className="MainSteps__content">
        <div className="MainSteps__title">
          <Trans>Как это работает</Trans>
        </div>
        <Steps
          collapsible={false}
          withSend={false}
          className="MainSteps__steps"
        >
          {mainPageStepsContent}
        </Steps>
      </div>
    </div>
  );
}

export default MainSteps;
